import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getPhotos, getCategories } from '../config/api';
import Lightbox from './Lightbox';
import CategoryFilterMenu from './CategoryFilterMenu';

export default function Gallery() {
  const LIMIT = 20;
  const observerTarget = useRef(null);

  const [photos,       setPhotos]       = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [activeId,     setActiveId]     = useState(null);
  const [activeName,   setActiveName]   = useState('ALL');
  const [lbOpen,       setLbOpen]       = useState(false);
  const [lbIdx,        setLbIdx]        = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [offset,       setOffset]       = useState(0);
  const [hasMore,      setHasMore]      = useState(true);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const [isWarmingUp,  setIsWarmingUp]  = useState(false);

  const loadMorePhotos = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const categoryParam = activeId || null;
      const response = await getPhotos(categoryParam, offset, LIMIT);
      const newPhotos = response.data?.data || [];
      const meta = response.data?.meta || {};

      setPhotos(prev => [...prev, ...newPhotos]);
      setOffset(prev => prev + LIMIT);
      setHasMore(meta.hasMore || false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, activeId, offset, LIMIT]);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, c] = await Promise.all([
          getPhotos(null, 0, LIMIT),
          getCategories()
        ]);

        const photoData = Array.isArray(p.data?.data) ? p.data.data : [];
        const meta = p.data?.meta || {};

        setPhotos(photoData);
        setCategories(Array.isArray(c.data?.data) ? c.data.data : []);
        setOffset(LIMIT);
        setHasMore(meta.hasMore || (meta.total > LIMIT));
      } catch (e) {
        console.error(e);
        setPhotos([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (!observerTarget.current || loading) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMorePhotos();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, loadMorePhotos]);

  // Warmup detection: show message if loading takes > 3 seconds
  useEffect(() => {
    if (loading && photos.length === 0) {
      const warmupTimer = setTimeout(() => {
        if (loading && photos.length === 0) {
          setIsWarmingUp(true);
        }
      }, 3000);

      return () => clearTimeout(warmupTimer);
    } else {
      setIsWarmingUp(false);
    }
  }, [loading, photos.length]);

  const handleFilter = async (cat) => {
    setLoading(true);
    setActiveName(cat ? cat.name : 'ALL');
    setActiveId(cat ? cat.id : null);
    setOffset(0);
    setHasMore(true);

    try {
      const p = await getPhotos(cat ? cat.id : null, 0, LIMIT);
      const photoData = Array.isArray(p.data?.data) ? p.data.data : [];
      const meta = p.data?.meta || {};

      setPhotos(photoData);
      setOffset(LIMIT);
      setHasMore(meta.hasMore || (meta.total > LIMIT));
    } catch (e) {
      console.error(e);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const btnStyle = (isActive) => ({
    padding: '6px 18px',
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    borderRadius: 20,
    cursor: 'pointer',
    fontFamily: 'var(--font-body)',
    border: isActive ? 'none' : '1px solid var(--color-border)',
    background: isActive ? 'var(--color-accent)' : 'transparent',
    color: isActive ? '#fff' : 'var(--color-muted)',
  });

  return (
    <div style={{ background: 'var(--color-bg)', paddingBottom: 40 }}>
      <style>{`
        .gallery-photo-wrapper {
          position: relative;
          cursor: pointer;
        }

        .gallery-photo-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0);
          transition: background 0.3s ease;
          display: flex;
          align-items: flex-end;
          padding: 12px;
          opacity: 0;
        }

        .gallery-photo-wrapper:hover .gallery-photo-overlay {
          background: rgba(0, 0, 0, 0.4);
          opacity: 1;
        }

        .gallery-photo-title {
          color: #fff;
          font-family: var(--font-body);
          font-size: 12px;
        }

        .filter-button {
          padding: 8px 12px;
          fontSize: 14px;
          fontFamily: var(--font-body);
          cursor: pointer;
          border: none;
          background: transparent;
          color: var(--color-muted);
          transition: color 0.2s ease;
        }

        .filter-button:hover {
          color: var(--color-text);
        }

        .filter-button.active {
          color: var(--color-accent);
        }
      `}</style>

      <div style={{ padding: '24px 32px 12px' }}>
        <div className="gallery-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '40px', fontWeight: 400, color: 'var(--color-text)', margin: 0 }}>
              Gallery
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', color: 'var(--color-muted)', fontSize: 13, margin: 0 }}>
              A collection of life's real moments
            </p>
          </div>
          <button
            className={`filter-button ${activeId ? 'active' : ''}`}
            onClick={() => setMenuOpen(true)}
            style={{ flexShrink: 0 }}
          >
            ⋮ {activeName}
          </button>
        </div>
      </div>

      <div className="category-pills" style={{ padding: '0 32px 12px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button style={btnStyle(activeName === 'ALL')} onClick={() => handleFilter(null)}>
          ALL
        </button>
        {categories.map(c => (
          <button key={c.id} style={btnStyle(activeId === c.id)} onClick={() => handleFilter(c)}>
            {c.name}
          </button>
        ))}
      </div>

      <div style={{ padding: '0 32px 16px', fontSize: 12, color: 'var(--color-muted)', fontFamily: 'var(--font-body)' }}>
        Showing {photos.length} photo{photos.length !== 1 ? 's' : ''}
      </div>

      {loading && (
        <div style={{ padding: 60, textAlign: 'center', fontFamily: 'var(--font-body)' }}>
          <div style={{ color: 'var(--color-muted)', marginBottom: '12px' }}>
            Loading...
          </div>
          {isWarmingUp && (
            <div style={{
              color: 'var(--color-text)',
              fontSize: '14px',
              lineHeight: '1.6',
              maxWidth: '500px',
              margin: '0 auto',
              padding: '0 20px'
            }}>
              Loading your photos... The server is waking up, this may take up to 30 seconds on first visit.
            </div>
          )}
        </div>
      )}

      {!loading && photos.length === 0 && (
        <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-muted)', fontFamily: 'var(--font-body)' }}>
          No photos in this category yet.
        </div>
      )}

      {!loading && photos.length > 0 && (
        <>
          <div style={{ padding: '0 32px', columns: 3, columnGap: 6 }}>
            {photos.map((photo, i) => (
              <div
                key={photo.id}
                className="gallery-photo-wrapper"
                onClick={() => { setLbIdx(i); setLbOpen(true); }}
                style={{ marginBottom: 6, breakInside: 'avoid' }}
              >
                <img
                  src={photo.thumbnail_url}
                  alt={photo.title || ''}
                  style={{ width: '100%', display: 'block' }}
                  loading="lazy"
                />
                <div className="gallery-photo-overlay">
                  <span className="gallery-photo-title">
                    {photo.title || photo.category?.name || ''}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Loading more indicator */}
          {loadingMore && (
            <div style={{ textAlign: 'center', padding: 20, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-muted)' }}>
              Loading more...
            </div>
          )}

          {/* Intersection observer target */}
          <div ref={observerTarget} style={{ height: 20 }} />
        </>
      )}

      {lbOpen && (
        <Lightbox
          photos={photos}
          startIndex={lbIdx}
          onClose={() => setLbOpen(false)}
        />
      )}

      <CategoryFilterMenu
        isOpen={menuOpen}
        categories={categories}
        activeCategory={activeId ? categories.find(c => c.id === activeId) : null}
        onSelect={(cat) => {
          handleFilter(cat);
          setMenuOpen(false);
        }}
        onClose={() => setMenuOpen(false)}
      />
    </div>
  );
}